/**
 * Whether a term has already happened, judged by its dates rather than by the
 * stored `status`.
 *
 * `CourseTerm.status` is only nudged to `past` by the daily cron
 * (`markConcludedTerms` on the worker), so between a term's last day and the
 * next morning's run the stored value still says `upcoming`. The admin reads
 * the dates for the same reason the website does (`isTermConcluded` in
 * `course-card-props`): what the user sees should not depend on when a cron
 * last ran.
 *
 * Terms are entered as Prague wall-clock time, so the comparison happens on
 * Prague calendar days — a term earlier today is not over yet.
 */
export type TermDateLike = { startsAt: string | null; endsAt: string | null }

const PRAGUE_DAY = new Intl.DateTimeFormat('en-CA', {
	timeZone: 'Europe/Prague',
	year: 'numeric',
	month: '2-digit',
	day: '2-digit',
})

/** `YYYY-MM-DD` in Europe/Prague; the strings compare lexicographically. */
function pragueDay(date: Date): string {
	return PRAGUE_DAY.format(date)
}

export function isTermConcluded(dates: readonly TermDateLike[], now = new Date()): boolean {
	const today = pragueDay(now)
	const days = dates
		.map(date => date.endsAt ?? date.startsAt)
		.filter((value): value is string => typeof value === 'string' && value.length > 0)
		.map(value => new Date(value))
		.filter(date => !Number.isNaN(date.getTime()))
	// No dates yet → it cannot have happened.
	if (days.length === 0) return false
	return days.every(date => pragueDay(date) < today)
}

/** Midnight of today's Prague calendar day, as an instant — for Contember date filters. */
export function pragueTodayStartIso(now = new Date()): string {
	const parts = new Intl.DateTimeFormat('en-CA', {
		timeZone: 'Europe/Prague',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		timeZoneName: 'longOffset',
	}).formatToParts(now)
	const value = (type: Intl.DateTimeFormatPartTypes) => parts.find(part => part.type === type)?.value ?? ''
	const offset = value('timeZoneName').replace('GMT', '') || '+01:00'
	return new Date(`${value('year')}-${value('month')}-${value('day')}T00:00:00${offset}`).toISOString()
}

/**
 * „Termín, který teprve bude" jako Contember where klauzule — stav `upcoming` a aspoň jedno
 * datum dneškem počínaje.
 *
 * Číst jen uložený `status` nestačí: na `past` ho překlápí až noční cron, takže mezi posledním
 * dnem termínu a ránem by termín pořád platil za nadcházející. Proto stejné pravidlo jako
 * {@link isTermConcluded}, jen vyjádřené dotazem — a jedno pro všechna místa, která se ptají
 * (výpis termínů, dlaždice na dashboardu), aby dvě obrazovky netvrdily každá něco jiného.
 */
export function upcomingTermWhere(now = new Date()): Record<string, unknown> {
	return { status: { eq: 'upcoming' }, dates: { startsAt: { gte: pragueTodayStartIso(now) } } }
}
